import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import Layout from '../components/Layout';
import StyledTableContainer from '../components/StyledTableContainer';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';

// Константы
const TASK_PRIORITIES = {
  low: { label: 'Низкий', color: '#4CAF50', icon: '🟢' },
  medium: { label: 'Средний', color: '#FF9800', icon: '🟡' },
  high: { label: 'Высокий', color: '#FF5722', icon: '🟠' },
  urgent: { label: 'Срочный', color: '#F44336', icon: '🔴' }
};

const TASK_STATUSES = {
  new: { label: 'Новая', color: '#2196F3', icon: '🆕' },
  in_progress: { label: 'В работе', color: '#FF9800', icon: '⏳' },
  completed: { label: 'Выполнена', color: '#4CAF50', icon: '✅' },
  overdue: { label: 'Просрочена', color: '#F44336', icon: '⚠️' }
};

const TasksPage = () => {
  // ✅ Подключение к SSE для real-time обновлений
  useRealtimeSSE();

  // Основные состояния
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Фильтры и поиск
  const [filters, setFilters] = useState({
    search: '',
    status: [],
    priority: [],
    assigned_to: ''
  });

  // Состояния диалогов
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Текущая задача для операций
  const [selectedTask, setSelectedTask] = useState(null);

  // Форма задачи
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'medium',
    status: 'new',
    assignedTo: '',
    linkedEntity: {}
  });

  // Состояния UI
  const [alert, setAlert] = useState({ show: false, type: 'info', message: '' });
  const [stats, setStats] = useState({});

  // Загрузка данных при монтировании
  useEffect(() => {
    loadTasks();
    loadStats();
  }, [page, filters]);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'info', message: '' }), 5000);
  };

  const loadTasks = async () => {
    try {
      setLoading(true);

      const params = {
        page,
        per_page: 20,
        ...filters
      };

      // Преобразуем массивы в строки для API
      if (filters.status.length > 0) {
        params.status = filters.status.join(',');
      }
      if (filters.priority.length > 0) {
        params.priority = filters.priority.join(',');
      }

      const response = await api.get('/tasks', { params });
      setTasks(response.data);
      setTotalPages(response.pagination?.pages || 1);
      setTotalCount(response.pagination?.total || 0);
    } catch (error) {
      showAlert('error', 'Ошибка загрузки задач');
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Подсчитываем статистику из текущих задач
      const statusCounts = tasks.reduce((acc, task) => {
        acc[task.status] = (acc[task.status] || 0) + 1;
        return acc;
      }, {});
      setStats(statusCounts);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreateTask = async () => {
    try {
      const payload = {
        ...taskForm,
        dueDate: taskForm.dueDate || null
      };

      const response = await api.post('/tasks', payload);

      if (response.status === 201) {
        showAlert('success', 'Задача создана успешно');
        setCreateDialog(false);
        resetTaskForm();
        loadTasks();
        loadStats();
      }
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Ошибка создания задачи');
    }
  };

  const handleUpdateTask = async () => {
    try {
      const payload = {
        ...taskForm,
        dueDate: taskForm.dueDate || null
      };

      const response = await api.put(`/tasks/${selectedTask.id}`, payload);

      if (response.status === 200) {
        showAlert('success', 'Задача обновлена');
        setEditDialog(false);
        resetTaskForm();
        loadTasks();
      }
    } catch (error) {
      showAlert('error', error.response?.data?.message || 'Ошибка обновления задачи');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту задачу?')) {
      return;
    }

    try {
      await api.delete(`/tasks/${taskId}`);
      showAlert('success', 'Задача удалена');
      loadTasks();
      loadStats();
    } catch (error) {
      showAlert('error', 'Ошибка удаления задачи');
    }
  };

  const resetTaskForm = () => {
    setTaskForm({
      title: '',
      description: '',
      dueDate: '',
      priority: 'medium',
      status: 'new',
      assignedTo: '',
      linkedEntity: {}
    });
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      priority: task.priority || 'medium',
      status: task.status || 'new',
      assignedTo: task.assignedTo || '',
      linkedEntity: task.linkedEntity || {}
    });
    setEditDialog(true);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const getPriorityChip = (priority) => {
    const config = TASK_PRIORITIES[priority] || TASK_PRIORITIES.medium;
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: `${config.color}20`,
          color: config.color,
          fontWeight: 'bold'
        }}
        icon={<span>{config.icon}</span>}
      />
    );
  };

  const getStatusChip = (status) => {
    const config = TASK_STATUSES[status] || TASK_STATUSES.new;
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: `${config.color}20`,
          color: config.color,
          fontWeight: 'bold'
        }}
        icon={<span>{config.icon}</span>}
      />
    );
  };

  return (
    <Layout>
      {/* Алерты */}
      {alert.show && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert({ show: false, type: 'info', message: '' })}
        >
          {alert.message}
        </Alert>
      )}

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {Object.entries(TASK_STATUSES).map(([status, config]) => (
          <Grid item xs={12} sm={6} md={3} key={status}>
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white'
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" component="div">
                      {stats[status] || 0}
                    </Typography>
                    <Typography variant="body2">
                      {config.label}
                    </Typography>
                  </Box>
                  <Box sx={{ fontSize: '2rem' }}>
                    {config.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Поиск и фильтры */}
      <Paper sx={{
        p: 2,
        mb: 3,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Поиск задач..."
            variant="outlined"
            size="small"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1 }} />
            }}
            sx={{
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
              }
            }}
          />

          <Button
            startIcon={<FilterIcon />}
            onClick={() => setFiltersOpen(!filtersOpen)}
            sx={{ color: 'white' }}
          >
            Фильтры
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
            sx={{
              background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)'
              }
            }}
          >
            Создать задачу
          </Button>
        </Box>

        <Collapse in={filtersOpen}>
          <Box mt={2} display="flex" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Статус</InputLabel>
              <Select
                multiple
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '& .MuiSvgIcon-root': { color: 'white' }
                }}
              >
                {Object.entries(TASK_STATUSES).map(([value, config]) => (
                  <MenuItem key={value} value={value}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Приоритет</InputLabel>
              <Select
                multiple
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '& .MuiSvgIcon-root': { color: 'white' }
                }}
              >
                {Object.entries(TASK_PRIORITIES).map(([value, config]) => (
                  <MenuItem key={value} value={value}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Collapse>
      </Paper>

      {/* Индикатор загрузки */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Таблица задач */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Задача</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Статус</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Приоритет</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Срок</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Исполнитель</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow key={task.id}>
                <TableCell sx={{ color: 'white' }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {task.title}
                    </Typography>
                    {task.description && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {task.description.length > 50
                          ? `${task.description.substring(0, 50)}...`
                          : task.description
                        }
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{getStatusChip(task.status)}</TableCell>
                <TableCell>{getPriorityChip(task.priority)}</TableCell>
                <TableCell sx={{ color: 'white' }}>{formatDate(task.dueDate)}</TableCell>
                <TableCell sx={{ color: 'white' }}>
                  {task.assignedTo ? (
                    <Chip
                      label={task.assignedTo}
                      size="small"
                      icon={<PersonIcon />}
                      sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      Не назначен
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Редактировать">
                    <IconButton
                      onClick={() => handleEditTask(task)}
                      sx={{ color: 'rgba(255,255,255,0.7)' }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить">
                    <IconButton
                      onClick={() => handleDeleteTask(task.id)}
                      sx={{ color: 'rgba(255,107,139,0.7)' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Пагинация */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2}>
          <Button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            sx={{ color: 'white' }}
          >
            Назад
          </Button>
          <Typography sx={{ color: 'white' }}>
            Страница {page} из {totalPages}
          </Typography>
          <Button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            sx={{ color: 'white' }}
          >
            Вперед
          </Button>
        </Box>
      )}

      {/* Диалог создания задачи */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white'
          }
        }}
      >
        <DialogTitle>Создать новую задачу</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Название задачи"
                fullWidth
                variant="outlined"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Описание"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Срок выполнения"
                type="date"
                fullWidth
                variant="outlined"
                value={taskForm.dueDate}
                onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Приоритет</InputLabel>
                <Select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                >
                  {Object.entries(TASK_PRIORITIES).map(([value, config]) => (
                    <MenuItem key={value} value={value}>{config.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Исполнитель (ID пользователя)"
                fullWidth
                variant="outlined"
                value={taskForm.assignedTo}
                onChange={(e) => setTaskForm({ ...taskForm, assignedTo: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Отмена
          </Button>
          <Button
            onClick={handleCreateTask}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)'
              }
            }}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог редактирования задачи */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white'
          }
        }}
      >
        <DialogTitle>Редактировать задачу</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Название задачи"
                fullWidth
                variant="outlined"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Описание"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Срок выполнения"
                type="date"
                fullWidth
                variant="outlined"
                value={taskForm.dueDate}
                onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Приоритет</InputLabel>
                <Select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                >
                  {Object.entries(TASK_PRIORITIES).map(([value, config]) => (
                    <MenuItem key={value} value={value}>{config.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Статус</InputLabel>
                <Select
                  value={taskForm.status}
                  onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                >
                  {Object.entries(TASK_STATUSES).map(([value, config]) => (
                    <MenuItem key={value} value={value}>{config.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Исполнитель (ID пользователя)"
                fullWidth
                variant="outlined"
                value={taskForm.assignedTo}
                onChange={(e) => setTaskForm({ ...taskForm, assignedTo: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Отмена
          </Button>
          <Button
            onClick={handleUpdateTask}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)'
              }
            }}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TasksPage;
